import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ManualLoader } from "src/components";
import {
  useGetRenewalTermsQuery,
  useGetUserQuery,
} from "src/store/services/privateApi";
import { getCurrentLease } from "src/helpers/helperLease";
import tokens from "src/shared/theme/tokens";
import {
  RenewalWizardShell,
  renewalWizardFooterSx,
} from "./RenewalWizardShell";

const formatRent = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);

const formatDateRange = (start: string, end: string) => {
  if (!start || !end) return "";
  const opts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  const s = new Date(`${start}T12:00:00`);
  const e = new Date(`${end}T12:00:00`);
  return `${s.toLocaleDateString(undefined, opts)} – ${e.toLocaleDateString(
    undefined,
    opts
  )}`;
};

/**
 * COPS Phase 2 — term selection matching the Renewal Offer PDF table
 * inside the renewal wizard.
 */
export const RenewalTermsScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: userData } = useGetUserQuery(null);
  const currentLease = getCurrentLease(userData);
  const leaseId = currentLease?.id ?? "";

  const { data, isLoading, isError } = useGetRenewalTermsQuery(leaseId, {
    skip: !leaseId,
  });

  const terms = data?.terms ?? [];
  const preferredId = useMemo(
    () => terms.find((term) => term.isPreferred)?.termId ?? null,
    [terms]
  );
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && data && !data.eligible) {
      navigate("/home", { replace: true });
    }
  }, [data, isLoading, navigate]);

  useEffect(() => {
    if (selectedTermId || terms.length === 0) return;
    setSelectedTermId(preferredId ?? terms[0].termId);
  }, [preferredId, selectedTermId, terms]);

  if (!leaseId || isLoading) {
    return <ManualLoader />;
  }

  if (isError || !data?.eligible) {
    return <ManualLoader />;
  }

  const onContinue = () => {
    if (!selectedTermId || !data.offerId) return;
    const selected = terms.find((term) => term.termId === selectedTermId);
    if (!selected) return;
    try {
      sessionStorage.setItem(
        `renewalTerm:${leaseId}`,
        JSON.stringify({
          offerId: data.offerId,
          term: selected,
          selectedAt: new Date().toISOString(),
        })
      );
    } catch {
      // ignore quota / private mode
    }
    navigate("/renewal/renew/next");
  };

  const headerSx = {
    display: isNarrow ? "none" : "grid",
    gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 0.7fr) minmax(0, 1.2fr)",
    gap: 2,
    px: 1.5,
    py: 1,
    borderBottom: `1px solid ${tokens.colors.border}`,
    color: "text.secondary",
    typography: "caption",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  } as const;

  const footer = (
    <Box sx={renewalWizardFooterSx}>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => navigate("/renewal")}
        sx={{ borderColor: tokens.colors.primary }}
      >
        {t("renewal.steps.back")}
      </Button>
      {terms.length === 0 ? (
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/home")}
        >
          {t("renewal.stub.backHome")}
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedTermId}
          onClick={onContinue}
          sx={{ minWidth: 140 }}
        >
          {t("renewal.terms.continue")}
        </Button>
      )}
    </Box>
  );

  return (
    <RenewalWizardShell path="renew" activeStep={1} footer={footer}>
      <Typography
        component="h1"
        sx={{
          fontFamily: tokens.fonts.display,
          fontWeight: 700,
          fontSize: { xs: 22, sm: 28 },
          color: tokens.colors.primaryDeep,
          mb: 1,
        }}
      >
        {t("renewal.terms.title")}
      </Typography>
      <Typography sx={{ mb: 1, color: tokens.colors.inkSecondary }}>
        {t("renewal.terms.subtitle")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: tokens.colors.inkMuted }}>
        {t("renewal.terms.rentOnlyNote")}
      </Typography>

      {terms.length === 0 ? (
        <Typography>{t("renewal.terms.empty")}</Typography>
      ) : (
        <Box
          sx={{
            backgroundColor: tokens.colors.surface,
            borderRadius: `${tokens.radius.md}px`,
            border: `1px solid ${tokens.colors.border}`,
            boxShadow: tokens.shadows.card,
            overflow: "hidden",
          }}
        >
          <FormControl component="fieldset" fullWidth>
            <Box sx={{ ...headerSx, px: 2 }}>
              <Box sx={{ pl: 4.5 }}>{t("renewal.terms.colTerm")}</Box>
              <Box>{t("renewal.terms.colRent")}</Box>
              <Box>{t("renewal.terms.colDates")}</Box>
            </Box>
            <RadioGroup
              value={selectedTermId ?? ""}
              onChange={(event) => setSelectedTermId(event.target.value)}
              name="renewal-term"
            >
              {terms.map((term) => {
                const selected = term.termId === selectedTermId;
                return (
                  <FormControlLabel
                    key={term.termId}
                    value={term.termId}
                    control={<Radio />}
                    sx={{
                      m: 0,
                      py: 1.5,
                      px: 2,
                      alignItems: isNarrow ? "flex-start" : "center",
                      borderBottom: `1px solid ${tokens.colors.border}`,
                      bgcolor: selected
                        ? tokens.colors.primarySoft
                        : "transparent",
                      "&:last-of-type": { borderBottom: "none" },
                      "& .MuiFormControlLabel-label": {
                        flex: 1,
                        ml: 0.5,
                        width: "100%",
                      },
                    }}
                    label={
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: isNarrow
                            ? "1fr"
                            : "minmax(0, 1.4fr) minmax(0, 0.7fr) minmax(0, 1.2fr)",
                          gap: isNarrow ? 0.5 : 2,
                          width: "100%",
                          alignItems: "baseline",
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {t("renewal.terms.termLabel", {
                              count: term.months,
                            })}
                          </Typography>
                          {term.isPreferred && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: tokens.colors.primary,
                                fontWeight: 600,
                              }}
                            >
                              {t("renewal.terms.preferred")}
                            </Typography>
                          )}
                          {isNarrow && (
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", mt: 0.25 }}
                            >
                              {formatRent(term.monthlyRent)}{" "}
                              {t("renewal.terms.perMonth")}
                            </Typography>
                          )}
                          {isNarrow && (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {formatDateRange(term.startDate, term.endDate)}
                            </Typography>
                          )}
                        </Box>
                        {!isNarrow && (
                          <Typography sx={{ fontWeight: 600 }}>
                            {formatRent(term.monthlyRent)}
                          </Typography>
                        )}
                        {!isNarrow && (
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            {formatDateRange(term.startDate, term.endDate)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
        </Box>
      )}
    </RenewalWizardShell>
  );
};

/** Placeholder until Phase 3 profile update / PR create. */
export const RenewalTermSelectedStubScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const footer = (
    <Box sx={renewalWizardFooterSx}>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => navigate("/renewal/renew")}
        sx={{ borderColor: tokens.colors.primary }}
      >
        {t("renewal.steps.back")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={() => navigate("/home")}
      >
        {t("renewal.stub.backHome")}
      </Button>
    </Box>
  );

  return (
    <RenewalWizardShell path="renew" activeStep={2} footer={footer}>
      <Typography
        component="h1"
        sx={{
          fontFamily: tokens.fonts.display,
          fontWeight: 700,
          fontSize: { xs: 22, sm: 28 },
          color: tokens.colors.primaryDeep,
          mb: 1,
        }}
      >
        {t("renewal.terms.nextTitle")}
      </Typography>
      <Typography sx={{ color: tokens.colors.inkSecondary }}>
        {t("renewal.terms.nextBody")}
      </Typography>
    </RenewalWizardShell>
  );
};
